import { PayerLanePath } from '../../services/payer-routing';
import { Variables } from '../base/variables';
import {
    mdiAlertCircleOutline,
    mdiEmailOutline,
    mdiFax,
    mdiFileDocumentOutline,
    mdiMailboxOutline,
    mdiShareVariantOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import * as S from './payer-path.styles';

/**
 * How a kind of data leaves the platform, as the design's pill.
 *
 * One component for the list columns and the detail panel so a row and its expanded panel
 * cannot disagree about a payer's routing — the thing they most need to agree on.
 *
 * It lives in `core` rather than in the support screen because the provider app renders the
 * same routing and cannot import from `enter-support`. Two copies of a chip that states where
 * a claim goes is two things that can disagree about routing (NS-1).
 *
 * Only `Not routed` is coloured. Electronic is highlighted because it is the good outcome;
 * the paper channels are deliberately neutral rather than warnings — posting a claim is
 * normal, not a problem. Red is reserved for the one state that means nothing can be sent.
 */

const ICON: Record<PayerLanePath, string> = {
    EDI: mdiShareVariantOutline,
    Letter: mdiMailboxOutline,
    Fax: mdiFax,
    Email: mdiEmailOutline,
    Statement: mdiFileDocumentOutline,
    'Not routed': mdiAlertCircleOutline,
};

export function PathChip({ path }: { path: PayerLanePath }): React.ReactElement {
    return (
        <S.Chip tone={path === 'EDI' ? 'electronic' : path === 'Not routed' ? 'blocked' : 'paper'}>
            <Icon path={ICON[path]} size={0.55} />
            {path}
        </S.Chip>
    );
}

/** The chip plus the destination lines under it, as the list columns render them. */
export function PathCell({
    path,
    destination,
    detail,
}: {
    path: PayerLanePath;
    destination?: string;
    detail?: string;
}): React.ReactElement {
    return (
        <S.Cell>
            <PathChip path={path} />
            {destination && <S.Destination title={destination}>{destination}</S.Destination>}
            {detail && <S.Detail title={detail}>{detail}</S.Detail>}
        </S.Cell>
    );
}

export const UNKNOWN_PATH = '…';

/** Renders `Variables`-coloured text for the count badges the design puts on EMR payers. */
export function CountBadge({ count }: { count: number }): React.ReactElement {
    return <S.Count empty={count === 0}>{count}</S.Count>;
}

